import React, { useState } from 'react';
import { useSpring, animated } from 'react-spring';
import styled from 'styled-components';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// 🎨 Estilo psicodélico
const FormContainer = styled.div`
  background: linear-gradient(45deg, #ff69b4, #8a2be2, #00ff00, #ff4500);
  background-size: 400% 400%;
  animation: gradient 10s ease infinite;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: white;
  font-family: 'Courier New', Courier, monospace;
  overflow-y: auto;

  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const FormWrapper = styled(animated.form)`
  background: rgba(0, 0, 0, 0.7);
  border-radius: 15px;
  padding: 20px;
  width: 80%;
  max-width: 600px;
  box-shadow: 0 0 15px 5px rgb(255, 255, 255);
  backdrop-filter: blur(10px);
  margin-top: 30px;
`;

const Logo = styled.img`
  width: 60%;
  max-width: 200px;
  height: auto;
  margin-bottom: 0.3px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const Label = styled.label`
  font-size: 1.2em;
  margin-bottom: 10px;
  display: block;
`;

const Select = styled.select`
  background-color: #ff6347;
  border: none;
  color: white;
  padding: 10px;
  margin: 10px 0;
  border-radius: 5px;
  font-size: 1.1em;
  cursor: pointer;
  width: 100%;  

  &:hover {
    background-color: #ff4500;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 5px 2px rgb(255, 255, 255);
    background-color: #ff4500;
  }

  transition: all 0.3s ease; 
`;

const Button = styled.button`
  background-color: #32cd32;
  border: none;
  color: white;
  padding: 10px;
  margin: 10px 0;
  border-radius: 5px;
  font-size: 1.1em;
  width: 100%;
  display: block;

  &:hover {
    transform: scale(1.1);
    background-color: #228b22;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  width: 100%;
`;

const ModalInput = styled.input`
  background-color: #ff6347;
  border: none;
  color: white;
  padding: 10px;
  margin: 5px 0;
  border-radius: 5px;
  font-size: 1.1em;
  width: 100%;

  &:focus {
    outline: none;
    box-shadow: 0 0 5px 2px rgba(0, 0, 0, 0.7);
  }

  @media (max-width: 600px) {
    font-size: 1em;
    padding: 8px;
  }
`;

const Form = () => {
  const [formData, setFormData] = useState({
    usuarios: '',
    funcionalidades: '',
    expectativas: '',
    experiencia: '',
  });

  const [whatsapp, setWhatsapp] = useState('');
  const [nome, setNome] = useState('');
  const [isWhatsappStep, setIsWhatsappStep] = useState(false);

  const animationProps = useSpring({
    opacity: 1,
    transform: 'scale(1)',
    from: { opacity: 0, transform: 'scale(0.8)' },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const { usuarios, funcionalidades, expectativas, experiencia } = formData;
    return usuarios && funcionalidades && expectativas && experiencia;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      toast.error("Por favor, preencha todas as perguntas antes de continuar.");
      return;
    }
  
    if (!whatsapp || whatsapp.length !== 11) {
      toast.error("Por favor, insira um número de WhatsApp válido com 11 dígitos.");
      return;
    }
  
    const formattedWhatsapp = whatsapp.startsWith('55') ? whatsapp : '55' + whatsapp;
  
    try {
      const response = await fetch('https://lingering-bush-4911.pingmegatron.workers.dev/api/users/form-submit', {
        method: 'POST',
        body: JSON.stringify({
          nome,
          whatsapp: formattedWhatsapp,
          empresa: 'Empresa Teste',
          ...formData, // ← isso espalha usuários, funcionalidades, etc diretamente
        }),
        headers: { 'Content-Type': 'application/json' },
      });
  
      const result = await response.json();
      console.log(result);
  
      if (response.status === 200 && result.message === 'Usuário já existe') {
        toast.info("Usuário já cadastrado. Redirecionando para o WhatsApp...");
        setTimeout(() => {
          window.location.href = result.redirect;
        }, 3000);
        return;
      }
  
      if (response.status === 201 && result.message === 'Usuário cadastrado com sucesso') {
        const senhaExtraida = result.senha || 'senha não informada'; // Para garantir caso a API não envie
  
        alert(`🎉 Cadastro realizado com sucesso!\n\nSua senha é: ${senhaExtraida}\n\nClique abaixo para fazer login:\nhttps://megatroncardapio.pages.dev/login`);
  
        // Resetar campos
        setWhatsapp('');
        setFormData({
          usuarios: '',
          funcionalidades: '',
          expectativas: '',
          experiencia: '',
        });
        setIsWhatsappStep(false);
      }
  
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      toast.error('Erro ao enviar. Tente novamente mais tarde.');
    }
  };
  

  return (
    <FormContainer>
      <Logo src="/images/logo.png" alt="Logo Psicodélico" />

      <FormWrapper style={animationProps} onSubmit={handleSubmit}>
        <Label>1. Quem são os principais usuários do sistema?</Label>
        <Select name="usuarios" value={formData.usuarios} onChange={handleChange} required>
          <option value="">Selecione</option>
          <option value="A">Equipe de produção (cozinha, caixa, entrega)</option>
          <option value="B">Gerentes e líderes de equipes</option>
          <option value="C">Usuários finais</option>
          <option value="D">Todos os membros da organização</option>
        </Select>

        <Label>2. Quais funcionalidades essenciais você precisa no sistema?</Label>
        <Select name="funcionalidades" value={formData.funcionalidades} onChange={handleChange} required>
          <option value="">Selecione</option>
          <option value="A">Gestão financeira</option>
          <option value="B">Automação e integração</option>
          <option value="C">Relatórios e gráficos</option>
          <option value="D">Personalização de interface</option>
          <option value="E">Integrações com terceiros</option>
        </Select>

        <Label>3. O que você espera que o software faça por você?</Label>
        <Select name="expectativas" value={formData.expectativas} onChange={handleChange} required>
          <option value="">Selecione</option>
          <option value="A">Automatizar processos</option>
          <option value="B">Melhorar comunicação</option>
          <option value="C">Gerenciar clientes e vendas</option>
          <option value="D">Tomada de decisões</option>
          <option value="E">Treinamento e suporte</option>
        </Select>

        <Label>4. Já usou algum software parecido?</Label>
        <Select name="experiencia" value={formData.experiencia} onChange={handleChange} required>
          <option value="">Selecione</option>
          <option value="A">ERP para gestão</option>
          <option value="B">Nunca usei</option>
          <option value="C">Automação de marketing</option>
          <option value="D">CRM de vendas</option>
          <option value="E">Gestão de projetos</option>
        </Select>

        {isWhatsappStep ? (
          <InputContainer>
            <ModalInput
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Seu nome completo"
            />
            <ModalInput
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="WhatsApp (apenas números)"
              maxLength="11"
            />
            <Button type="submit">Enviar</Button>
          </InputContainer>
        ) : (
          <Button
            type="button"
            onClick={() => {
              if (!validateForm()) {
                toast.error("Por favor, responda todas as perguntas antes de continuar.");
              } else {
                setIsWhatsappStep(true);
              }
            }}
          >
            Próximo: Informar WhatsApp e Nome
          </Button>
        )}
      </FormWrapper>

      <ToastContainer />
    </FormContainer>
  );
};

export default Form;
